import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { DESKTOP_MIN_PX, isDesktopViewport } from '../hooks/useIsDesktop';

const MediaViewerContext = createContext();

export const useMediaViewer = () => {
  const context = useContext(MediaViewerContext);
  if (!context) {
    throw new Error('useMediaViewer must be used within MediaViewerProvider');
  }
  return context;
};

// Null outside the provider instead of throwing: for shared components that only READ the chat's viewer state when
// it exists (FullPageMediaViewer's Chat Files analytics, CEL-3473 round 5).
export const useOptionalMediaViewer = () => useContext(MediaViewerContext) || null;

export const MediaViewerProvider = ({ children }) => {
  const [viewerState, setViewerState] = useState({
    mode: 'closed',          // 'closed' | 'split' | 'fullscreen'
    rightPanelContent: 'media',  // 'media' | 'hc_review' | 'hire_proposal' — what the right panel shows
    currentMediaIndex: 0,
    mediaFiles: [],
    panelSize: 50,
    isDesktop: isDesktopViewport(),
    // Issue 2: Track origin for panel↔fullscreen sync
    openedFromPanel: false,  // Did fullscreen come from split mode?
    fromGallery: false,      // Chat Files (CEL-3473): file opened from the gallery → Esc / "← Files" return to it
    // One viewer (Oct 16 2026): a file clicked in a chat message, opened over the Files list exactly as from the gallery
    // (same list, same header, ← opens Files at it). Only ✕ and Esc differ: they close and leave the user in the chat.
    fromChat: false,
    videoState: {
      timestamp: 0,
      wasPlaying: false
    }
  });

  // Chat Files gallery (CEL-3473): the small-screen sheet, and the tile to re-focus when stepping back.
  const [gallerySheetOpen, setGallerySheetOpen] = useState(false);
  const [galleryFocusBlob, setGalleryFocusBlob] = useState(null);
  // Latest state for the resize handler, which decides the gallery's form OUTSIDE the state updater (S4: setting the
  // sheet flag is a side effect, and updaters must stay pure).
  const latestRef = useRef({ viewerState, gallerySheetOpen });
  latestRef.current = { viewerState, gallerySheetOpen };
  // Chat Files in an AI employee chat (CEL-3473 round 6, Nitish ruling 1): the desktop gallery borrows the Employee
  // Computer's slot. While this is set (MediaViewerPanel: an employee chat whose Computer is open, the same rule as its
  // keepSplitOnClose), closing the gallery keeps the slot split and empties it, so the panel falls through to the
  // Computer again instead of closing the slot. A ref: it only matters at the moment of a close.
  const galleryKeepsSplitRef = useRef(false);
  const setGalleryKeepsSplit = useCallback((keep) => {
    galleryKeepsSplitRef.current = !!keep;
  }, []);
  // One viewer (Oct 16 2026): counts the opens a user asks for (a file, Files, ← to Files). A chat click that is still
  // loading its file drops itself when this moved: the user opened something else meanwhile. Automatic slot changes (the
  // Employee Computer's split, HC review polling, the employee viewer's fill) don't count. A ref: read at that moment.
  const openSeqRef = useRef(0);
  const viewerOpenSeq = useCallback(() => openSeqRef.current, []);

  // Resize: track isDesktop AND reconcile an OPEN panel across the breakpoint crossing
  // (agent_docs/right_panel_coherence_prd.md §1.5). Shrinking with a split open used to leave
  // state 'split' while the grid slot collapsed to a hidden 0fr column — the open file went
  // invisible (audio/video kept playing with no UI; iPad rotation). HC review carried a local
  // fix in HumanComputerContext; this generic version supersedes it for EVERY slot rider.
  useEffect(() => {
    const handleResize = () => {
      // Chat Files (CEL-3473, review S4): the open gallery keeps its form across the crossing. The split gallery
      // becomes the sheet going down (iPad rotation), the sheet becomes the split gallery going up.
      const nowDesktop = window.innerWidth >= DESKTOP_MIN_PX;
      const { viewerState: cur, gallerySheetOpen: sheetOpen } = latestRef.current;
      if (nowDesktop !== cur.isDesktop) {
        if (!nowDesktop && cur.mode === 'split' && cur.rightPanelContent === 'gallery') {
          setGallerySheetOpen(true);
          setViewerState(prev => ({ ...prev, isDesktop: false, mode: 'closed', rightPanelContent: 'media', openedFromPanel: false, fromGallery: false, fromChat: false }));
          return;
        }
        if (nowDesktop && sheetOpen) {
          setGallerySheetOpen(false);
          setViewerState(prev => ({
            ...prev,
            isDesktop: true,
            mode: 'split',
            rightPanelContent: 'gallery',
            mediaFiles: [],
            currentMediaIndex: 0,
            openedFromPanel: false,
            fromGallery: false,
            fromChat: false,
            videoState: { timestamp: 0, wasPlaying: false }
          }));
          return;
        }
      }
      setViewerState(prev => {
        const isDesktop = window.innerWidth >= DESKTOP_MIN_PX;
        if (isDesktop === prev.isDesktop) return prev; // no crossing — no re-render churn
        const hasContent = prev.rightPanelContent === 'hc_review' || prev.rightPanelContent === 'hire_proposal' || prev.mediaFiles.length > 0;
        if (!isDesktop && prev.mode === 'split') {
          // Desktop → small: the split doesn't exist below the breakpoint. Real content
          // transitions to the direct fullscreen takeover; an EMPTY slot (Employee Computer /
          // blank fall-through) closes instead — ChatPage re-summons the EC as its own takeover.
          // Never feed the fullscreen viewer an empty file list.
          return { ...prev, isDesktop, mode: hasContent ? 'fullscreen' : 'closed', openedFromPanel: false };
        }
        if (isDesktop && prev.mode === 'fullscreen') {
          // Small → desktop: the direct takeover becomes the split (the desktop default).
          return { ...prev, isDesktop, mode: hasContent ? 'split' : 'closed', openedFromPanel: false };
        }
        return { ...prev, isDesktop };
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const savedSize = localStorage.getItem('cellcog_media_panel_size');
    if (savedSize) {
      setViewerState(prev => ({
        ...prev,
        panelSize: parseInt(savedSize, 10)
      }));
    }
  }, []);

  // The right panel (media viewer / HC review / employee dashboard) is fully decoupled from the global
  // sidebar: opening or closing a panel never collapses or expands the sidebar. The sidebar is its own
  // thing — it only moves on explicit user interaction (toggle button, Cmd/Ctrl+\, post-auth expand).
  const openMedia = useCallback((mediaFiles, initialIndex = 0) => {
    const mode = viewerState.isDesktop ? 'split' : 'fullscreen';
    openSeqRef.current += 1;

    setGallerySheetOpen(false);  // Chat Files (S5): a file opened from the chat replaces the gallery sheet
    setViewerState(prev => ({
      ...prev,
      mode,
      rightPanelContent: 'media',  // Reset to media when opening media
      fromGallery: false,
      fromChat: false,
      mediaFiles,
      currentMediaIndex: initialIndex,
      openedFromPanel: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, [viewerState.isDesktop]);

  // ==================== Chat Files gallery (CEL-3473) ====================
  // Desktop: the gallery rides the right slot as rightPanelContent 'gallery' (split). A file opened from it
  // carries fromGallery, so Esc and "← Files" step back to the gallery instead of closing (✕ still closes).
  // Below the breakpoint the gallery is a bottom sheet (gallerySheetOpen) and a file opens full screen;
  // closing that viewer reopens the sheet.
  const openGallery = useCallback(() => {
    openSeqRef.current += 1;
    if (!viewerState.isDesktop) {
      setGallerySheetOpen(true);
      return;
    }
    setViewerState(prev => ({
      ...prev,
      mode: 'split',
      rightPanelContent: 'gallery',
      mediaFiles: [],
      currentMediaIndex: 0,
      openedFromPanel: false,
      fromGallery: false,
      fromChat: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, [viewerState.isDesktop]);

  const closeGallery = useCallback(() => {
    setGallerySheetOpen(false);
    setGalleryFocusBlob(null);
    setViewerState(prev => (prev.rightPanelContent !== 'gallery' ? prev : {
      ...prev,
      mode: galleryKeepsSplitRef.current && prev.isDesktop ? 'split' : 'closed',
      rightPanelContent: 'media',
      mediaFiles: [],
      currentMediaIndex: 0,
      openedFromPanel: false,
      fromGallery: false,
      fromChat: false
    }));
  }, []);

  const openMediaFromGallery = useCallback((files, initialIndex = 0) => {
    if (!files || files.length === 0) return;
    openSeqRef.current += 1;
    const desktop = viewerState.isDesktop;
    setGallerySheetOpen(false);
    setViewerState(prev => ({
      ...prev,
      mode: desktop ? 'split' : 'fullscreen',
      rightPanelContent: 'media',
      mediaFiles: files,
      currentMediaIndex: initialIndex,
      openedFromPanel: false,
      fromGallery: true,
      fromChat: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, [viewerState.isDesktop]);

  // One viewer (Oct 16 2026): a file clicked in a chat message, over the Files list (useOpenFromChat built it and found
  // the file in it). The same open as openMediaFromGallery; only the origin differs (✕ / Esc close instead of stepping
  // back to the gallery, which was never open).
  const openMediaFromChat = useCallback((files, initialIndex = 0) => {
    if (!files || files.length === 0) return;
    openSeqRef.current += 1;
    const desktop = viewerState.isDesktop;
    setGallerySheetOpen(false);
    setViewerState(prev => ({
      ...prev,
      mode: desktop ? 'split' : 'fullscreen',
      rightPanelContent: 'media',
      mediaFiles: files,
      currentMediaIndex: initialIndex,
      openedFromPanel: false,
      fromGallery: false,
      fromChat: true,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, [viewerState.isDesktop]);

  // Chat Files round 6b: an employee list's viewer walks the whole list with placeholders for the rows not loaded yet;
  // as they load, the list is swapped in place. The file on screen stays on screen (found again by its blob; a
  // placeholder keeps its place, which is its true index). A no-op unless a file of the Files list is open (from the
  // gallery or, one viewer, from the chat).
  const refreshGalleryMedia = useCallback((files) => {
    if (!files || files.length === 0) return;
    setViewerState(prev => {
      if (!(prev.fromGallery || prev.fromChat) || prev.mode === 'closed' || prev.rightPanelContent !== 'media') return prev;
      const current = prev.mediaFiles[prev.currentMediaIndex];
      let index = prev.currentMediaIndex;
      if (current && current.blobName) {
        const found = files.findIndex(f => f.blobName === current.blobName);
        if (found >= 0) index = found;
      }
      return { ...prev, mediaFiles: files, currentMediaIndex: Math.min(index, files.length - 1) };
    });
  }, []);

  // "←" in the viewer: Files, at the file on screen (desktop: the split gallery; below the breakpoint: the sheet). The
  // same for a file opened from the gallery and one opened from the chat (one viewer, ruling 3).
  const backToGallery = useCallback(() => {
    openSeqRef.current += 1;
    const current = viewerState.mediaFiles[viewerState.currentMediaIndex];
    setGalleryFocusBlob(current?.blobName || null);
    const desktop = viewerState.isDesktop;
    setViewerState(prev => ({
      ...prev,
      mode: desktop ? 'split' : 'closed',
      rightPanelContent: desktop ? 'gallery' : 'media',
      mediaFiles: [],
      currentMediaIndex: 0,
      openedFromPanel: false,
      fromGallery: false,
      fromChat: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
    if (!desktop) setGallerySheetOpen(true);
  }, [viewerState]);

  // Issue 2: Enhanced closeMedia that respects openedFromPanel.
  // `opts.revertToDashboard` (AI-employee chats): instead of closing the panel, KEEP it open in split
  // mode and just clear the media — the panel then re-renders the employee dashboard (its
  // `employeeId && !hasMedia` branch). No close→reopen animation; the panel never goes blank. For
  // single chats this is unset → today's close behavior is unchanged. See
  // agent_docs/ai_employees_unified_chat_architecture_design.md (#5).
  const closeMedia = useCallback((opts = {}) => {
    const { mode, openedFromPanel } = viewerState;

    if (opts.revertToDashboard) {
      setViewerState(prev => ({
        ...prev,
        mode: 'split',                 // stay open
        rightPanelContent: 'media',    // panel falls through to the dashboard when mediaFiles is empty
        fromGallery: false,            // Chat Files (round 6): a gallery file closed here is closed, not "from the gallery"
        fromChat: false,
        mediaFiles: [],
        currentMediaIndex: 0,
        openedFromPanel: false,
        videoState: { timestamp: 0, wasPlaying: false },
      }));
      return;
    }

    // If we're in fullscreen and came from panel, return to split mode
    if (mode === 'fullscreen' && openedFromPanel) {
      setViewerState(prev => ({
        ...prev,
        mode: 'split',
        // Keep current index and media files - they stay in sync
        // Reset video state since we're changing modes
        videoState: {
          timestamp: prev.videoState.timestamp,
          wasPlaying: false  // Don't auto-play when returning to panel
        }
      }));
      return;
    }

    // Otherwise, close completely
    setViewerState(prev => ({
      ...prev,
      mode: 'closed',
      rightPanelContent: 'media',
      fromGallery: false,
      fromChat: false,
      mediaFiles: [],
      currentMediaIndex: 0,
      openedFromPanel: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, [viewerState]);

  // Issue 2: Enhanced toggleFullscreen that tracks origin
  const toggleFullscreen = useCallback((videoState = null) => {
    setViewerState(prev => {
      if (prev.mode === 'split') {
        // Going FROM panel TO fullscreen
        return {
          ...prev,
          mode: 'fullscreen',
          openedFromPanel: true,  // Remember we came from panel
          videoState: videoState ? {
            timestamp: videoState.currentTime || 0,
            wasPlaying: videoState.isPlaying || false
          } : prev.videoState
        };
      } else if (prev.mode === 'fullscreen') {
        // Going FROM fullscreen back
        if (prev.openedFromPanel) {
          // Return to split mode
          return {
            ...prev,
            mode: 'split',
            // Keep openedFromPanel true so subsequent toggles work correctly
            // until media is fully closed
          };
        } else {
          // Was opened directly to fullscreen (mobile or direct open), close completely
          return {
            ...prev,
            mode: 'closed',
            mediaFiles: [],
            currentMediaIndex: 0,
            openedFromPanel: false,
            fromGallery: false,
            fromChat: false,
            videoState: { timestamp: 0, wasPlaying: false }
          };
        }
      }
      return prev;
    });
  }, []);

  // Minimize desktop fullscreen back to the split (doctrine §1.4: ✕ always CLOSES; MINIMIZE is
  // the separate step-down). No-op below the breakpoint (no split exists) or when not fullscreen.
  const minimizeToSplit = useCallback(() => {
    setViewerState(prev => {
      if (prev.mode !== 'fullscreen' || !prev.isDesktop) return prev;
      return { ...prev, mode: 'split', openedFromPanel: false };
    });
  }, []);

  const navigateMedia = useCallback((direction) => {
    setViewerState(prev => {
      const { currentMediaIndex, mediaFiles } = prev;
      // Clamp at boundaries — no wrap-around
      if (direction === 'next' && currentMediaIndex >= mediaFiles.length - 1) {
        return prev; // At end boundary — no state change
      }
      if (direction === 'prev' && currentMediaIndex <= 0) {
        return prev; // At start boundary — no state change
      }
      const newIndex = direction === 'next'
        ? currentMediaIndex + 1
        : currentMediaIndex - 1;
      return {
        ...prev,
        currentMediaIndex: newIndex,
        // Reset video state when navigating to different media
        videoState: { timestamp: 0, wasPlaying: false }
      };
    });
  }, []);

  const setPanelSize = useCallback((size) => {
    localStorage.setItem('cellcog_media_panel_size', size.toString());
    setViewerState(prev => ({
      ...prev,
      panelSize: size
    }));
  }, []);

  // Issue 2: Update video state (for timestamp sync)
  const updateVideoState = useCallback((timestamp, isPlaying = false) => {
    setViewerState(prev => ({
      ...prev,
      videoState: {
        timestamp: timestamp || 0,
        wasPlaying: isPlaying
      }
    }));
  }, []);

  // Hard reset media viewer to closed state (for unmount/navigation cleanup).
  // Unlike closeMedia which has smart fullscreen↔split transitions,
  // this unconditionally resets everything to closed.
  const resetMedia = useCallback(() => {
    setGallerySheetOpen(false);
    setGalleryFocusBlob(null);
    setViewerState(prev => ({
      ...prev,
      mode: 'closed',
      rightPanelContent: 'media',
      fromGallery: false,
      fromChat: false,
      mediaFiles: [],
      currentMediaIndex: 0,
      openedFromPanel: false,
      videoState: { timestamp: 0, wasPlaying: false }
    }));
  }, []);

  // The full-screen viewer's close (✕, Esc below the breakpoint, backdrop click: FullPageMediaViewer routes all three
  // through onClose → ChatWindow.handleFullscreenClose). Chat Files (CEL-3473, review B1): below the breakpoint a file
  // opened from the gallery returns to the sheet; everything else closes entirely, as before (doctrine §1.4). One viewer:
  // a file opened from the chat (fromChat) closes entirely too, leaving the user in the chat with no sheet open.
  const closeFullscreen = useCallback(() => {
    if (viewerState.fromGallery && !viewerState.isDesktop) {
      backToGallery();
      return;
    }
    resetMedia();
  }, [viewerState.fromGallery, viewerState.isDesktop, backToGallery, resetMedia]);

  // Open HC review panel — uses the same split-mode grid as media viewer
  // No-op if HC review is already open in the correct mode (prevents state churn from 3s polling)
  const openHCReview = useCallback(() => {
    setGallerySheetOpen(false);  // Chat Files (S5): the sheet (z 2100) would cover the takeover (z 1100)
    setViewerState(prev => {
      const targetMode = prev.isDesktop ? 'split' : 'fullscreen';

      // Already showing HC review in the correct mode — no-op
      if (prev.rightPanelContent === 'hc_review' && prev.mode === targetMode) {
        return prev;
      }

      return {
        ...prev,
        mode: targetMode,
        rightPanelContent: 'hc_review',
        openedFromPanel: false,
      };
    });
  }, []);

  // Toggle HC review between split ↔ fullscreen (mirrors toggleFullscreen for media)
  const toggleHCReviewFullscreen = useCallback(() => {
    setViewerState(prev => {
      if (prev.rightPanelContent !== 'hc_review') return prev;

      if (prev.mode === 'split') {
        // Split → fullscreen
        return { ...prev, mode: 'fullscreen', openedFromPanel: true };
      } else if (prev.mode === 'fullscreen' && prev.openedFromPanel) {
        // Fullscreen → split (came from panel)
        return { ...prev, mode: 'split' };
      } else if (prev.mode === 'fullscreen' && !prev.openedFromPanel) {
        // Fullscreen opened directly (mobile) — close entirely
        return {
          ...prev,
          mode: 'closed',
          rightPanelContent: 'media',
          openedFromPanel: false,
        };
      }
      return prev;
    });
  }, []);

  // Close HC review panel — resets to closed
  const closeHCReview = useCallback(() => {
    setViewerState(prev => {
      // Already closed — no-op
      if (prev.rightPanelContent !== 'hc_review') {
        return prev;
      }

      return {
        ...prev,
        mode: 'closed',
        rightPanelContent: 'media',
        openedFromPanel: false,
      };
    });
  }, []);

  // ==================== Hire proposal (agent_docs/employee_initiated_hire_prd.md FR2) ====================
  // The prefilled hire form rides the SAME slot as HC review, with the same three operations.
  // Open — split on desktop, direct fullscreen below the breakpoint. No-op when already showing.
  const openHireProposal = useCallback(() => {
    setGallerySheetOpen(false);  // Chat Files (S5): same as openHCReview
    setViewerState(prev => {
      const targetMode = prev.isDesktop ? 'split' : 'fullscreen';
      if (prev.rightPanelContent === 'hire_proposal' && prev.mode === targetMode) {
        return prev;
      }
      return {
        ...prev,
        mode: targetMode,
        rightPanelContent: 'hire_proposal',
        openedFromPanel: false,
      };
    });
  }, []);

  // Toggle split ↔ fullscreen (mirrors toggleHCReviewFullscreen).
  const toggleHireProposalFullscreen = useCallback(() => {
    setViewerState(prev => {
      if (prev.rightPanelContent !== 'hire_proposal') return prev;
      if (prev.mode === 'split') {
        return { ...prev, mode: 'fullscreen', openedFromPanel: true };
      } else if (prev.mode === 'fullscreen' && prev.openedFromPanel) {
        return { ...prev, mode: 'split' };
      } else if (prev.mode === 'fullscreen' && !prev.openedFromPanel) {
        // Fullscreen opened directly (mobile) — close entirely
        return {
          ...prev,
          mode: 'closed',
          rightPanelContent: 'media',
          openedFromPanel: false,
        };
      }
      return prev;
    });
  }, []);

  // Close — resets to closed; no-op when the slot shows something else (never disturbs media/HC).
  const closeHireProposal = useCallback(() => {
    setViewerState(prev => {
      if (prev.rightPanelContent !== 'hire_proposal') {
        return prev;
      }
      return {
        ...prev,
        mode: 'closed',
        rightPanelContent: 'media',
        openedFromPanel: false,
      };
    });
  }, []);

  // Open the right panel in split mode for an AI Employee chat (compact rail OR full dashboard —
  // EmployeeDashboardContext.panelView decides WHAT renders in the slot; this only opens it).
  // rightPanelContent stays 'media' so opening an artifact (openMedia) shows it, and closing it
  // (closeMedia → no media) makes MediaViewerPanel fall back to the employee panel.
  // No-op if already in split mode (prevents churn on re-render).
  const openEmployeePanel = useCallback(() => {
    setViewerState(prev => {
      if (!prev.isDesktop) return prev; // dashboard panel is a desktop split surface
      // No-op unless CLOSED (CEL-795 review fix): this function's job is to ENSURE the right
      // slot is open — never to change what's showing. The old `mode === 'split'` guard let a
      // FULLSCREEN expand get instantly demoted back to split: ChatPage's employee-route effect
      // depends on `mode`, re-fires on the fullscreen transition, calls this, and the old guard
      // fell through to `mode: 'split'` — killing fullscreen the moment it opened (the
      // "expand does nothing / closes itself" employee-chat bug).
      if (prev.mode !== 'closed') return prev; // already open (split OR fullscreen)
      return { ...prev, mode: 'split', rightPanelContent: 'media', openedFromPanel: false };
    });
  }, []);

  const value = {
    ...viewerState,
    openMedia,
    closeMedia,
    resetMedia,
    closeFullscreen,
    toggleFullscreen,
    navigateMedia,
    setPanelSize,
    updateVideoState,
    openHCReview,
    closeHCReview,
    toggleHCReviewFullscreen,
    openHireProposal,
    closeHireProposal,
    toggleHireProposalFullscreen,
    openEmployeePanel,
    minimizeToSplit,
    // Chat Files gallery (CEL-3473)
    gallerySheetOpen,
    galleryFocusBlob,
    openGallery,
    closeGallery,
    openMediaFromGallery,
    openMediaFromChat,
    viewerOpenSeq,
    backToGallery,
    setGalleryKeepsSplit,
    refreshGalleryMedia,
  };

  return (
    <MediaViewerContext.Provider value={value}>
      {children}
    </MediaViewerContext.Provider>
  );
};
